import clsx from 'clsx';
import React from 'react';

interface DropdownCustomToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 *
 * forwardRef is required here because:
 * 1. React Bootstrap's Dropdown component needs direct DOM access to the toggle button
 * 2. The dropdown library must measure the button's position and dimensions to properly
 *    position the dropdown menu relative to the toggle button
 * 3. Without forwardRef, the ref would be lost and the dropdown positioning would fail
 * 4. The library also uses the ref for:
 *    - Click-outside detection to close the dropdown
 *    - Keyboard navigation (arrow keys, escape key handling)
 *    - Focus management when opening/closing the dropdown
 *
 * Take a look to the official docs:
 * https://react-bootstrap.netlify.app/docs/components/dropdowns/#custom-dropdown-components
 */
export const DropdownCustomToggle = React.forwardRef<HTMLButtonElement, DropdownCustomToggleProps>(
  ({
    children, onClick, className, ...props
  }, ref) => (
    <button
      type="button"
      className={clsx('btn', className)}
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      // We want to forward all props to the button
      // In order to make it flexible and customizable
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children}
    </button>
  ));
